// src/stores/useCollaborationStore.js
import { create } from "zustand";
import { fetchEvents, postComment, fetchNotifications, markNotificationsRead, patchEventMetadata, subscribeToStream } from "../api/collabApi";

// REM-422. Reconnect backoff for an outage: 5, 10, 20, 40, then 60 s thereafter.
export const STREAM_RETRY_BASE_MS = 5_000;
export const STREAM_RETRY_MAX_MS = 60_000;
import { useQcStore, writerRoleFor } from "./useQcStore";
import { useAuthStore } from "./useAuthStore";
import { toast } from "./useToastStore";
import { computeEffectiveOrgId } from "../api/fetchWithAuth";
import { router } from "../router";

// Navigate to the PadRekko tab and load the given case. Called from
// case_stage_transition toast onClick — the cross-store nav has to
// originate outside a React render tree, so we use the exported
// router instance + the useQcStore action directly.
function openCaseFromToast(caseId) {
  if (!caseId) return;
  try {
    router.navigate({ to: "/map/$tab", params: { tab: "padrekko" } });
  } catch (err) {
    console.warn("[collab/toast-open] navigate failed", err);
  }
  useQcStore.getState().loadCase(caseId);
}

export const useCollaborationStore = create((set, get) => ({

  // ─── Thread state ───────────────────────────────────────────────────────────
  // { [contextKey]: { events: [], loading: bool, error: string|null, loaded: bool } }
  threads: {},

  // { [contextKey]: [{ id, initials, color }] }
  members: {},

  // "project" | "org"
  contextScope: "project",

  // ─── Notification state ─────────────────────────────────────────────────────
  notifications: [],
  unreadCount: 0,
  notificationsLoading: false,

  // ─── Scope ─────────────────────────────────────────────────────────────────

  setScope: (scope) => set({ contextScope: scope }),

  // ─── Thread actions ─────────────────────────────────────────────────────────

  /**
   * Load events for a contextKey from the API.
   * No-ops if already loaded unless force=true.
   */
  loadThread: async (contextKey, { force = false } = {}) => {
    const existing = get().threads[contextKey];
    if (existing?.loaded && !force) return;

    set((s) => ({
      threads: {
        ...s.threads,
        [contextKey]: { events: existing?.events || [], loading: true, error: null, loaded: false },
      },
    }));

    try {
      const { events } = await fetchEvents(contextKey);
      set((s) => ({
        threads: {
          ...s.threads,
          [contextKey]: { events, loading: false, error: null, loaded: true },
        },
      }));
    } catch (err) {
      console.error("[collab] loadThread failed", err);
      set((s) => ({
        threads: {
          ...s.threads,
          [contextKey]: {
            events: existing?.events || [],
            loading: false,
            error: "Failed to load thread",
            loaded: false,
          },
        },
      }));
    }
  },

  /**
   * Post a reply to a top-level event. Optimistically appended to
   * the parent's replies array, replaced by the real event on success.
   */
  sendReply: async (contextKey, parentId, body, authorDisplay) => {
    const optimisticId = `optimistic_reply_${Date.now()}`;
    const optimistic = {
      id: optimisticId,
      event_type: "comment",
      parent_id: parentId,
      body,
      author_display: authorDisplay,
      created_at: new Date().toISOString(),
      unread: false,
      optimistic: true,
      replies: [],
    };

    // Append optimistic reply to the parent event's replies array
    set((s) => {
      const thread = s.threads[contextKey];
      if (!thread) return {};
      return {
        threads: {
          ...s.threads,
          [contextKey]: {
            ...thread,
            events: thread.events.map((e) =>
              e.id === parentId
                ? { ...e, replies: [...(e.replies || []), optimistic] }
                : e
            ),
          },
        },
      };
    });

    try {
      const real = await postComment(contextKey, body, { parent_id: parentId });

      // Replace optimistic reply with real one
      set((s) => {
        const thread = s.threads[contextKey];
        if (!thread) return {};
        return {
          threads: {
            ...s.threads,
            [contextKey]: {
              ...thread,
              events: thread.events.map((e) =>
                e.id === parentId
                  ? {
                      ...e,
                      replies: (e.replies || []).map((r) =>
                        r.id === optimisticId ? real : r
                      ),
                    }
                  : e
              ),
            },
          },
        };
      });
    } catch (err) {
      console.error("[collab] sendReply failed", err);
      // Remove optimistic reply on failure
      set((s) => {
        const thread = s.threads[contextKey];
        if (!thread) return {};
        return {
          threads: {
            ...s.threads,
            [contextKey]: {
              ...thread,
              events: thread.events.map((e) =>
                e.id === parentId
                  ? { ...e, replies: (e.replies || []).filter((r) => r.id !== optimisticId) }
                  : e
              ),
            },
          },
        };
      });
    }
  },

  /**
   * Post any event type (flag, link, system) from quick actions.
   * No optimistic update — these are low-frequency structured events.
   */
  postEvent: async (contextKey, { event_type, body = "", metadata = {} }) => {
    try {
      const real = await postComment(contextKey, body, { metadata, event_type });
      set((s) => {
        const thread = s.threads[contextKey] || { events: [], loading: false, error: null, loaded: true };
        return {
          threads: {
            ...s.threads,
            [contextKey]: { ...thread, events: [real, ...thread.events] },
          },
        };
      });
      return real;
    } catch (err) {
      console.error("[collab] postEvent failed", err);
    }
  },

  /**
   * Post a comment and optimistically prepend it to the thread.
   */
  sendComment: async (contextKey, body, authorDisplay) => {
    // Optimistic event — replaced by real data on success
    const optimisticId = `optimistic_${Date.now()}`;
    const optimistic = {
      id: optimisticId,
      event_type: "comment",
      body,
      author_display: authorDisplay,
      created_at: new Date().toISOString(),
      unread: false,
      optimistic: true,
    };

    set((s) => {
      const thread = s.threads[contextKey] || { events: [], loading: false, error: null, loaded: true };
      return {
        threads: {
          ...s.threads,
          [contextKey]: { ...thread, events: [optimistic, ...thread.events] },
        },
      };
    });

    try {
      const real = await postComment(contextKey, body);

      // Replace optimistic with real event
      set((s) => {
        const thread = s.threads[contextKey];
        if (!thread) return {};
        return {
          threads: {
            ...s.threads,
            [contextKey]: {
              ...thread,
              events: thread.events.map((e) => (e.id === optimisticId ? real : e)),
            },
          },
        };
      });
    } catch (err) {
      console.error("[collab] sendComment failed", err);
      // Remove optimistic on failure
      set((s) => {
        const thread = s.threads[contextKey];
        if (!thread) return {};
        return {
          threads: {
            ...s.threads,
            [contextKey]: {
              ...thread,
              events: thread.events.filter((e) => e.id !== optimisticId),
              error: "Failed to send comment",
            },
          },
        };
      });
    }
  },

  /**
   * Mark all events in a thread as read, both locally and on the server.
   */
  markRead: async (contextKey) => {
    const thread = get().threads[contextKey];
    if (!thread) return;

    // Collect unread IDs from both top-level events and nested replies
    const unreadIds = thread.events.flatMap((e) => [
      ...(e.unread ? [e.id] : []),
      ...(e.replies || []).filter((r) => r.unread).map((r) => r.id),
    ]);

    if (!unreadIds.length) return;

    // Optimistic local update
    set((s) => ({
      threads: {
        ...s.threads,
        [contextKey]: {
          ...s.threads[contextKey],
          events: s.threads[contextKey].events.map((e) => ({ ...e, unread: false })),
        },
      },
    }));

    try {
      await markNotificationsRead(unreadIds);
      // Refresh the global unread count so the TopNav badge updates
      await get().loadNotifications();
    } catch (err) {
      console.warn("[collab] markRead failed", err);
    }
  },

  // ─── Notification actions ────────────────────────────────────────────────────

  loadNotifications: async () => {
    set({ notificationsLoading: true });
    try {
      const { notifications, unread_count } = await fetchNotifications();
      set({ notifications, unreadCount: unread_count, notificationsLoading: false });
    } catch (err) {
      console.error("[collab] loadNotifications failed", err);
      set({ notificationsLoading: false });
    }
  },

  markAllRead: async () => {
    set({ unreadCount: 0 });
    try {
      await markNotificationsRead(null);
      set((s) => ({
        notifications: s.notifications.map((n) => ({ ...n, read: true })),
      }));
    } catch (err) {
      console.warn("[collab] markAllRead failed", err);
    }
  },

  /**
   * Resolve or reopen a flag event — updates metadata.status optimistically
   * then syncs to the server.
   */
  resolveFlag: async (contextKey, eventId, newStatus) => {
    // Optimistic update — find the event in any thread position
    const updateEvent = (events) =>
      events.map((e) => {
        if (e.id === eventId) {
          return { ...e, metadata: { ...e.metadata, status: newStatus } };
        }
        // Also check replies
        if (e.replies?.length) {
          return { ...e, replies: e.replies.map((r) =>
            r.id === eventId ? { ...r, metadata: { ...r.metadata, status: newStatus } } : r
          )};
        }
        return e;
      });

    set((s) => {
      const thread = s.threads[contextKey];
      if (!thread) return {};
      return {
        threads: {
          ...s.threads,
          [contextKey]: { ...thread, events: updateEvent(thread.events) },
        },
      };
    });

    try {
      await patchEventMetadata(eventId, { status: newStatus });
    } catch (err) {
      console.error("[collab] resolveFlag failed", err);
      // Revert on failure
      const revert = newStatus === "resolved" ? "open" : "resolved";
      set((s) => {
        const thread = s.threads[contextKey];
        if (!thread) return {};
        return {
          threads: {
            ...s.threads,
            [contextKey]: { ...thread, events: updateEvent(thread.events).map((e) =>
              e.id === eventId ? { ...e, metadata: { ...e.metadata, status: revert } } : e
            )},
          },
        };
      });
    }
  },

  // ─── SSE subscriptions ──────────────────────────────────────────────────────
  // { [contextKey]: unsubscribe fn }
  _subscriptions: {},
  // REM-422. Per contextKey: "live" | "reconnecting" | "stopped". Read by the
  // timeline so a stream that is not delivering says so on screen.
  streamStatus: {},
  // REM-422. Per contextKey: { attempt, timer } for the pending reconnect, so
  // the backoff can grow and so unsubscribeThread can cancel it.
  _retries: {},

  /**
   * Open an SSE subscription for a contextKey.
   * Closes any existing subscription for the same key first.
   * Incoming events are merged into the thread — optimistic duplicates
   * (matching id) are replaced, new events are prepended.
   */
  subscribeThread: (contextKey) => {
    const existing = get()._subscriptions[contextKey];
    if (existing) return; // already subscribed

    const unsubscribe = subscribeToStream(
      contextKey,
      // onEvent — called when the server pushes a new event
      (event) => {
        // Refresh notification count immediately on any new incoming event
        // so the TopNav bell updates without waiting for the 30s poll
        get().loadNotifications();

        // D2.4 + D3 — case-event side effects. Subtypes:
        //   case_stage_transition: refresh the loaded case (if it's
        //     this one) so the banner updates; refresh the picker list;
        //     fire a "you are the new writer" toast when applicable.
        //   case_created: refresh the picker list; fire an info toast
        //     announcing the new case the user has been added to.
        //   pad_detection_completed / pad_detection_failed (Day 4):
        //     refresh the loaded case so the inspector overlay clears
        //     and detection_status / findings reflect truth. No toast
        //     in v0 — overlay disappearing + findings appearing is the
        //     signal. Hook point preserved for future v1 toast.
        // Author suppression mirrors the existing bell isOwn pattern —
        // the user who triggered the action doesn't get notified about
        // their own action.
        const subtype = event.event_type === "system"
          ? event.metadata?.event_subtype
          : null;
        if (subtype === "pad_detection_completed" || subtype === "pad_detection_failed") {
          const meta = event.metadata || {};
          const caseInfo = useQcStore.getState().caseInfo;
          if (caseInfo?.id && caseInfo.id === meta.case_id) {
            // Day 4 follow-up: cascade fix. Lazy-per-pad is the default;
            // SSE-driven refresh preserves the inspector cursor so a pad
            // completing doesn't auto-advance the user to the next pending
            // pad (which would re-fire the kickoff useEffect and detect
            // all pads in sequence). Opt-in via case.config.
            const autoAdvance = caseInfo.config?.auto_advance_on_complete === true;
            useQcStore.getState().loadCase(caseInfo.id, { preserveCursor: !autoAdvance });
          }
          // Day 4 rail-visibility split:
          //   pad_detection_completed → bail before threads append. High
          //     volume + expected outcome; findings are already visible
          //     in the inspector and via audit queries.
          //   pad_detection_failed → fall through to threads append below
          //     so failures appear in the rail in real-time for retry
          //     triage. Backend GET /events filter excludes completed
          //     events from initial fetch (historical-data side).
          if (subtype === "pad_detection_completed") return;
        }
        if (
          subtype === "case_stage_transition"
          || subtype === "case_created"
          || subtype === "stage_reverse_transition"
        ) {
          const meta = event.metadata || {};
          const currentUserId = useAuthStore.getState().user?.id ?? null;
          const isAuthor = !!(currentUserId && event.author_id === currentUserId);

          // Refresh loaded case for any stage move (forward or reverse).
          // case_created skips this — a brand-new case isn't loaded
          // anywhere yet.
          if (
            subtype === "case_stage_transition"
            || subtype === "stage_reverse_transition"
          ) {
            const currentCaseId = useQcStore.getState().caseInfo?.id;
            if (currentCaseId && currentCaseId === meta.case_id) {
              useQcStore.getState().loadCase(currentCaseId, { preserveCursor: true });
            }
          }

          // Refresh picker list — covers all three subtypes. If the user
          // is viewing the picker, the list updates in place.
          useQcStore.getState().listCases({ limit: 20 });

          // Toast — skip author + non-actionable transitions.
          if (!isAuthor) {
            // No author_username fallback: it was an email address (REM-426).
            const actor = event.author_display || "Someone";
            const caseName = meta.case_name || "a case";
            if (subtype === "case_created") {
              toast.info(
                `${actor} created Case ${caseName}. You're a participant.`
              );
            } else if (subtype === "case_stage_transition") {
              // Toast only if the receiver is the new writer for to_stage.
              const targetWriter = writerRoleFor(meta.to_stage);
              if (targetWriter) {
                const effectiveOrgId = computeEffectiveOrgId();
                const me = (meta.participants || []).find(
                  (p) => p.org_id === effectiveOrgId
                );
                if (me && me.role === targetWriter) {
                  const caseId = meta.case_id;
                  toast.warning(
                    `${actor} handed off Case ${caseName} to you for review.`,
                    {
                      sticky: true,
                      onClick: () => openCaseFromToast(caseId),
                    }
                  );
                }
              }
            } else if (subtype === "stage_reverse_transition") {
              // Phase A — toast the receiver of a reverse handoff (the
              // user whose role is the new writer at the to_stage).
              const targetWriter = writerRoleFor(meta.to_stage);
              if (targetWriter) {
                const effectiveOrgId = computeEffectiveOrgId();
                const me = (meta.participants || []).find(
                  (p) => p.org_id === effectiveOrgId
                );
                if (me && me.role === targetWriter) {
                  const caseId = meta.case_id;
                  toast.warning(
                    `${actor} sent Case ${caseName} back to you for revision.`,
                    {
                      sticky: true,
                      onClick: () => openCaseFromToast(caseId),
                    }
                  );
                }
              }
            }
          }
        }

        // REM-576 — the handoff acts change who can see a case and what its
        // bar offers, so they refresh like a stage move: the loaded case (if
        // it is this one) and the case list, and a toast for anyone who did
        // not do it. A revoke at operator_review also moves the stage back,
        // and arrives as this one event.
        if (
          subtype === "case_released"
          || subtype === "case_shared"
          || subtype === "case_share_revoked"
        ) {
          const meta = event.metadata || {};
          const currentCaseId = useQcStore.getState().caseInfo?.id;
          if (currentCaseId && currentCaseId === meta.case_id) {
            useQcStore.getState().loadCase(currentCaseId, { preserveCursor: true });
          }
          useQcStore.getState().listCases({ limit: 20 });
          const currentUserId = useAuthStore.getState().user?.id ?? null;
          const isAuthor = !!(currentUserId && event.author_id === currentUserId);
          if (!isAuthor) {
            const actor = event.author_display || "Someone";
            const caseName = meta.case_name || "a case";
            const caseId = meta.case_id;
            const message = subtype === "case_released"
              ? `${actor} released Case ${caseName}.`
              : subtype === "case_shared"
                ? `${actor} shared Case ${caseName}.`
                : `${actor} revoked the share of Case ${caseName}.`;
            toast.info(message, { onClick: () => openCaseFromToast(caseId) });
          }
        }

        // REM-576 — the operator's org is told its access was withdrawn (the
        // deliberate D5 exception). The row names NO case id, by design: the
        // org can no longer see the case. So the list refreshes, an open case
        // of that name reloads (the server answers with its refusal, which the
        // view shows), and the toast says what happened. Nothing to open.
        if (subtype === "case_access_withdrawn") {
          const meta = event.metadata || {};
          const open = useQcStore.getState().caseInfo;
          if (open?.id && meta.case_name && open.name === meta.case_name) {
            useQcStore.getState().loadCase(open.id, { preserveCursor: true });
          }
          useQcStore.getState().listCases({ limit: 20 });
          toast.warning(`Access to Case ${meta.case_name || "a case"} was withdrawn.`, { sticky: true });
        }

        // Day 8 Phase A — case_note_added. Silent (no toast — notes are
        // higher-volume than stage events). Hydrate the note into useQcStore
        // if the case has notes loaded; the bell badge updates via the
        // existing collab_notifications path.
        if (subtype === "case_note_added") {
          const meta = event.metadata || {};
          if (meta.case_id && meta.note_id) {
            useQcStore.getState().appendNoteFromSSE({
              case_id: meta.case_id,
              note_id: meta.note_id,
            });
          }
        }

        // Phase 2 — per-finding kickback-loop events. Reconcile the loaded case
        // so the OTHER party sees the kickback / resubmit / modify / delete
        // without a manual reload. Mirrors the stage-transition reconciler
        // (loadCase, preserveCursor). Silent — the rail entry is the signal.
        if (
          subtype === "kicked_back_to_dh"
          || subtype === "dh_resubmitted_after_kickback"
          || subtype === "finding_modified_by_customer"
          || subtype === "finding_deleted_by_customer"
        ) {
          const meta = event.metadata || {};
          const currentCaseId = useQcStore.getState().caseInfo?.id;
          if (currentCaseId && currentCaseId === meta.case_id) {
            useQcStore.getState().loadCase(currentCaseId, { preserveCursor: true });
          }
        }

        set((s) => {
          const thread = s.threads[contextKey];
          if (!thread) return {};

          const events = thread.events || [];

          // Replace optimistic duplicate or prepend if genuinely new
          const exists = events.some((e) => e.id === event.id);
          const updated = exists
            ? events.map((e) => (e.id === event.id ? event : e))
            : [event, ...events];

          // If it's a reply, nest it under the parent instead
          if (event.parent_id) {
            const withReply = (events).map((e) =>
              e.id === event.parent_id
                ? {
                    ...e,
                    replies: (e.replies || []).some((r) => r.id === event.id)
                      ? (e.replies || []).map((r) => r.id === event.id ? event : r)
                      : [...(e.replies || []), event],
                  }
                : e
            );
            return {
              threads: {
                ...s.threads,
                [contextKey]: { ...thread, events: withReply },
              },
            };
          }

          return {
            threads: {
              ...s.threads,
              [contextKey]: { ...thread, events: updated },
            },
          };
        });
      },
      // onError — reconnect on an outage; STOP on a refusal.
      //
      // The retry below was unconditional and unbounded, which was harmless
      // only because nothing ever refused this stream permanently: the server
      // authorised once at connect and then held the connection open forever.
      // REM-66 ended that — the stream now closes when the token expires — so
      // the retry path can now reach an endpoint that will refuse every
      // attempt. Without a stop condition, a logged-out tab left open would
      // reconnect every 5 seconds indefinitely at roughly 0.2 req/s, which is
      // a worse outcome than the defect REM-66 fixed.
      //
      // REM-422: THE STOP CONDITION WAS readyState CLOSED, AND CLOSED IS NOT
      // ONLY A REFUSAL. A 500 while the database is down and a 502 while
      // admin-api restarts leave it CLOSED too, so any outage that outlasted
      // one 5 s retry stopped live updates for the rest of the tab's life,
      // with only a console.warn to say so. subscribeToStream now resolves
      // CLOSED by status (one probe): hardRefusal means 400/401/403/404 and
      // nothing else. An outage is retried with a backoff (5, 10, 20, 40, then
      // every 60 s) and no attempt cap, because an outage ends and a refusal
      // does not, and REM-66's concern was spinning on a refusal.
      (err, info) => {
        // The probe makes this report asynchronous. If the view unsubscribed,
        // or a newer stream replaced this one, while it was in flight, the
        // report is about a stream nobody holds, and acting on it would
        // resurrect a subscription the view already let go of.
        if (get()._subscriptions[contextKey] !== unsubscribe) return;
        get()._closeStream(contextKey);
        if (info?.hardRefusal) {
          console.warn(
            `[collab/stream] refused (HTTP ${info.status}); not retrying. The session is no longer ` +
              "valid for this stream — re-authentication or a reload is required.",
          );
          get()._clearRetry(contextKey);
          get()._setStreamStatus(contextKey, "stopped");
          return;
        }
        get()._scheduleRetry(contextKey);
      },
      // onOpen — the stream is delivering; a later drop starts the backoff over.
      () => {
        if (get()._subscriptions[contextKey] !== unsubscribe) return;
        get()._clearRetry(contextKey);
        get()._setStreamStatus(contextKey, "live");
      }
    );

    set((s) => ({
      _subscriptions: { ...s._subscriptions, [contextKey]: unsubscribe },
    }));
  },

  /**
   * Close the SSE subscription for a contextKey. This is the VIEW letting go
   * (CollaborationTimeline's effect cleanup), so it also cancels a pending
   * reconnect. Before REM-422 it did not, and a view unmounted during the 5 s
   * wait got its stream reopened anyway, with nobody reading it.
   */
  unsubscribeThread: (contextKey) => {
    get()._clearRetry(contextKey);
    get()._closeStream(contextKey);
    set((s) => {
      const status = { ...s.streamStatus };
      delete status[contextKey];
      return { streamStatus: status };
    });
  },

  // Close the EventSource only. Used by the error path, which is NOT the view
  // letting go and must leave the retry state for _scheduleRetry to grow.
  _closeStream: (contextKey) => {
    const unsubscribe = get()._subscriptions[contextKey];
    if (unsubscribe) {
      unsubscribe();
      set((s) => {
        const subs = { ...s._subscriptions };
        delete subs[contextKey];
        return { _subscriptions: subs };
      });
    }
  },

  _setStreamStatus: (contextKey, status) =>
    set((s) => ({ streamStatus: { ...s.streamStatus, [contextKey]: status } })),

  _clearRetry: (contextKey) => {
    const r = get()._retries[contextKey];
    if (r?.timer) clearTimeout(r.timer);
    if (r) {
      set((s) => {
        const retries = { ...s._retries };
        delete retries[contextKey];
        return { _retries: retries };
      });
    }
  },

  _scheduleRetry: (contextKey) => {
    const attempt = get()._retries[contextKey]?.attempt ?? 0;
    const delay = Math.min(STREAM_RETRY_BASE_MS * 2 ** attempt, STREAM_RETRY_MAX_MS);
    const timer = setTimeout(() => get().subscribeThread(contextKey), delay);
    set((s) => ({
      _retries: { ...s._retries, [contextKey]: { attempt: attempt + 1, timer } },
      streamStatus: { ...s.streamStatus, [contextKey]: "reconnecting" },
    }));
  },

  // ─── Members ────────────────────────────────────────────────────────────────

  setMembers: (contextKey, members) =>
    set((s) => ({
      members: { ...s.members, [contextKey]: members },
    })),

  // ─── Derived ────────────────────────────────────────────────────────────────

  getThreadEvents: (contextKey) =>
    get().threads[contextKey]?.events || [],

  getThreadLoading: (contextKey) =>
    get().threads[contextKey]?.loading ?? false,

  getThreadError: (contextKey) =>
    get().threads[contextKey]?.error ?? null,

  hasUnreadInContext: (contextKey) =>
    (get().threads[contextKey]?.events || []).some((e) => e.unread),
}));
